// Snapshot interpolation buffer.
//
// Transport-agnostic presentation helper: the network layer pushes arrivals;
// the renderer asks for interpolated remote poses / entity positions and the
// tick stamp the last frame actually showed. Keeping this out of `main.ts`
// makes the transport -> presentation boundary explicit and testable.

import type { NetEntity, NetSnapshot } from "./transport";

export interface SnapEntry {
  t: number;
  snap: NetSnapshot;
}

/** Shortest-path interpolation between two angles (radians). */
export function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  d = (((d + Math.PI) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
  return a + d * t;
}

export interface RemotePose {
  slot: number;
  origin: [number, number, number];
  yaw: number;
  pitch: number;
}

export class SnapshotBuffer {
  private entries: SnapEntry[] = [];

  /**
   * `interpMs` is a live getter (the client can retune the interpolation delay
   * at runtime: `?interp=`, debug, adaptive interp), so it is never a stale copy.
   */
  constructor(
    private readonly cap = 32,
    private readonly interpMs: () => number = () => 100,
  ) {}

  get length(): number {
    return this.entries.length;
  }

  /** Newest buffered snapshot, or null when empty. */
  latest(): NetSnapshot | null {
    return this.entries.length ? this.entries[this.entries.length - 1].snap : null;
  }

  push(t: number, snap: NetSnapshot): void {
    this.entries.push({ t, snap });
    if (this.entries.length > this.cap) this.entries.shift();
  }

  clear(): void {
    this.entries.length = 0;
  }

  /** The two buffered snapshots around `now - interpMs` and the blend fraction. */
  private pair(now: number): { a: SnapEntry; b: SnapEntry; f: number } | null {
    const n = this.entries.length;
    if (n === 0) return null;
    const renderT = now - this.interpMs();
    let a = this.entries[0];
    let b = this.entries[n - 1];
    if (renderT <= a.t) {
      b = a;
    } else if (renderT >= b.t) {
      a = b;
    } else {
      for (let i = 0; i < n - 1; i++) {
        if (this.entries[i].t <= renderT && renderT <= this.entries[i + 1].t) {
          a = this.entries[i];
          b = this.entries[i + 1];
          break;
        }
      }
    }
    const span = Math.max(1e-3, b.t - a.t);
    const f = Math.min(1, Math.max(0, (renderT - a.t) / span));
    return { a, b, f };
  }

  /**
   * The snapshot pair + fraction the last completed render used. `f` is encoded
   * 0..65535 to match the core's `RenderStamp.f: u16`.
   */
  stamp(now: number): { a: number; b: number; f: number } | null {
    const pair = this.pair(now);
    if (!pair) return null;
    return { a: pair.a.snap.tick, b: pair.b.snap.tick, f: Math.round(pair.f * 65535) };
  }

  /** Interpolated remote-player poses (rendering + name tags). */
  remoteStates(now: number, mySlot: number): RemotePose[] {
    if (mySlot < 0) return [];
    const pair = this.pair(now);
    if (!pair) return [];
    const { a, b, f } = pair;
    const aBySlot = new Map(a.snap.players.map((p) => [p.slot, p]));
    const out: RemotePose[] = [];
    for (const pb of b.snap.players) {
      if (pb.slot === mySlot) continue;
      const pa = aBySlot.get(pb.slot) ?? pb;
      out.push({
        slot: pb.slot,
        origin: [
          pa.o[0] + (pb.o[0] - pa.o[0]) * f,
          pa.o[1] + (pb.o[1] - pa.o[1]) * f,
          pa.o[2] + (pb.o[2] - pa.o[2]) * f,
        ],
        yaw: lerpAngle(pa.a[0], pb.a[0], f),
        pitch: pa.a[1] + (pb.a[1] - pa.a[1]) * f,
      });
    }
    return out;
  }

  /**
   * Projectiles interpolated between the same pair the remote players use, so
   * they move smoothly at any display refresh instead of stepping at the
   * snapshot rate. An entity that only exists in the newer snapshot is drawn at
   * its authority position.
   */
  interpolatedEntities(now: number, latest: NetEntity[], connected: boolean): NetEntity[] {
    if (!connected) return latest;
    const pair = this.pair(now);
    if (!pair) return latest;
    const { a, b, f } = pair;
    const aById = new Map((a.snap.entities ?? []).map((e) => [e.id, e]));
    const out: NetEntity[] = [];
    for (const eb of b.snap.entities ?? []) {
      const ea = aById.get(eb.id);
      if (!ea) {
        out.push(eb);
        continue;
      }
      out.push({
        ...eb,
        o: [
          ea.o[0] + (eb.o[0] - ea.o[0]) * f,
          ea.o[1] + (eb.o[1] - ea.o[1]) * f,
          ea.o[2] + (eb.o[2] - ea.o[2]) * f,
        ],
      });
    }
    return out;
  }
}
